/**
 * **配方目录** —— 内置的「命名变体」清单。
 *
 * ## 这个文件在体系里的位置
 *
 * ```text
 *   主题文档 recipes: { node: 'glass' }
 *        │  只是**一个名字**
 *        ▼
 *   applyTheme() 把名字写成 html[data-np-node="glass"]
 *        │
 *        ▼
 *   theme.css 里 [data-np-node='glass'] .np-node { … }  ← 怎么画由 CSS 说
 * ```
 *
 * 内核**不认识**这些名字的含义，它只负责搬运。所以：
 *
 * * 主题包可以自带 CSS 定义自己的配方（内核不用改一行）；
 * * 但**目录里的每个名字都必须有对应 CSS** —— 否则主题选了它却什么都不发生，
 *   而这种失败是**完全静默**的。`verify:theme` 逐条对账，就是防这个。
 *
 * ## ⚠️ 为什么目录要写在 TS 里，而不是让 CSS 自己声明
 *
 * 因为「有哪些变体可选」这件事要出现在**主题编辑器的 UI** 上。
 * 让 CSS 当真相源的话，UI 就得去解析样式表 —— 那比维护一张表脆弱得多。
 * 这张表是**声明**，CSS 是**实现**，两边由验证脚本绑在一起。
 */
import type { RecipeSlot } from '../types/theme'

export interface RecipeOption {
  value: string
  /** 中文label，直接显示在主题编辑器里 */
  label: string
  /** 一句话说明它长什么样 */
  hint: string
}

export interface RecipeSlotMeta {
  slot: RecipeSlot
  label: string
  hint: string
  /** 内置默认值。主题没写这个槽位时用它 */
  fallback: string
  options: readonly RecipeOption[]
}

/**
 * 全部槽位与取值。
 *
 * ⚠️ **顺序就是 UI 里的顺序**（先外壳后部件），别随手插。
 */
export const RECIPE_CATALOG: readonly RecipeSlotMeta[] = [
  {
    slot: 'shell',
    label: '窗口外壳',
    hint: '整个世界的地板与墙面',
    fallback: 'flat',
    options: [
      { value: 'flat', label: '平铺', hint: '单一底色，最省眼睛' },
      { value: 'layered', label: '层次', hint: '外壳比面板暗一档，靠明度分前后' },
      { value: 'glass', label: '玻璃', hint: '面板半透明 + 背景虚化' },
    ],
  },
  {
    slot: 'node',
    label: '节点主体',
    hint: '节点这块方块怎么填',
    fallback: 'gradient',
    options: [
      { value: 'flat', label: '纯色', hint: '一块平色，最干净' },
      { value: 'gradient', label: '渐变', hint: '自上而下的两段渐变（Blender 的观感）' },
      { value: 'glass', label: '毛玻璃', hint: '半透明 + 内部高光 + 背板虚化' },
      { value: 'outline', label: '描边', hint: '几乎不填色，全靠一圈描边' },
      { value: 'neon', label: '霓虹', hint: '暗底 + 强调色外发光' },
      { value: 'elevated', label: '浮起', hint: '多层投影，像纸片摞起来' },
    ],
  },
  {
    slot: 'header',
    label: '节点头',
    hint: '分类色的那块标题条',
    fallback: 'solid',
    options: [
      { value: 'solid', label: '纯色', hint: '分类色平铺（Blender 的观感）' },
      { value: 'gradient', label: '渐变', hint: '分类色做斜向渐变' },
      { value: 'accent-bar', label: '强调条', hint: '暗底 + 左侧一条分类色竖条' },
      { value: 'underline', label: '下划线', hint: '通体透明，只在底部画一条' },
      { value: 'minimal', label: '极简', hint: '不加任何底色，只留文字' },
    ],
  },
  {
    slot: 'row',
    label: '节点行',
    hint: '一行一行怎么分隔',
    fallback: 'plain',
    options: [
      { value: 'plain', label: '平铺', hint: '行与行之间什么都不加' },
      { value: 'striped', label: '斑马纹', hint: '隔一行给一点底色' },
      { value: 'divided', label: '分隔线', hint: '每行底部一条细线' },
      { value: 'comfortable', label: '宽松', hint: '加高行距，适合触屏' },
    ],
  },
  {
    slot: 'widget',
    label: '内联控件',
    hint: '输入框 / 开关 / 下拉',
    fallback: 'blender',
    options: [
      { value: 'blender', label: 'Blender', hint: '深底凹槽，边界靠内阴影' },
      { value: 'soft', label: '柔和', hint: '浅底 + 圆角，浮在节点上' },
      { value: 'outline', label: '描边', hint: '透明底 + 一圈边框' },
      { value: 'filled', label: '实心', hint: '用面板底色填满，几乎看不到框' },
    ],
  },
  {
    slot: 'socket',
    label: '端口形状',
    hint: '连线端点的外形',
    fallback: 'circle',
    options: [
      { value: 'circle', label: '圆形', hint: 'Blender 的经典圆点' },
      { value: 'square', label: '方形', hint: '圆角小方块，更像电路板' },
      { value: 'diamond', label: '菱形', hint: '旋转 45°，方向感更强' },
      { value: 'ring', label: '圆环', hint: '空心圆，配色更克制' },
    ],
  },
  {
    slot: 'state',
    label: '选中与活动',
    hint: '选中一个节点时怎么标出来',
    fallback: 'ring',
    options: [
      { value: 'ring', label: '描边环', hint: '外圈一圈实线（当前观感）' },
      { value: 'glow', label: '辉光', hint: '柔和外发光，没有硬边' },
      { value: 'shadow', label: '投影', hint: '加深投影，像被抬起来' },
      { value: 'thick', label: '加粗边框', hint: '直接把边框加粗，不加环' },
    ],
  },
  {
    slot: 'popup',
    label: '菜单与弹层',
    hint: '右键菜单 / 下拉 / 拾色器',
    fallback: 'solid',
    options: [
      { value: 'solid', label: '实心', hint: '不透，边界清楚' },
      { value: 'glass', label: '玻璃', hint: '半透明 + 背景虚化' },
      { value: 'outline', label: '描边', hint: '重描边、轻底色' },
    ],
  },
]

/** `{ node: 'glass' }` 这样的默认值全集 */
export const DEFAULT_RECIPES: Record<RecipeSlot, string> = Object.fromEntries(
  RECIPE_CATALOG.map((s) => [s.slot, s.fallback]),
) as Record<RecipeSlot, string>

/** 取某个槽位的目录项（找不到返回 undefined，不抛） */
export function recipeSlot(slot: string): RecipeSlotMeta | undefined {
  return RECIPE_CATALOG.find((s) => s.slot === slot)
}

/**
 * 这个取值是不是目录里的合法值。
 *
 * ⚠️ 返回 `false` **不代表主题是错的** —— 主题包可以自带 CSS 定义新配方。
 * 它只用来在主题编辑器里区分「内置」与「自定义」，以及给内建主题把门。
 */
export function isCataloguedRecipe(slot: RecipeSlot, value: string): boolean {
  return !!recipeSlot(slot)?.options.some((o) => o.value === value)
}

/** 把主题给的选择补齐成「每个槽位都有值」 */
export function withRecipeDefaults(selection: Partial<Record<RecipeSlot, string>> = {}): Record<
  RecipeSlot,
  string
> {
  return { ...DEFAULT_RECIPES, ...selection }
}
